import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { useState, useEffect, useContext, useMemo } from 'react';

import PageContainer from '~/shared/components/PageContainer';
import { VolumeResponse } from '~/shared/types/api';
import { useXFetch } from '~/shared/utils/useXFetch';
import Loader from '~/shared/components/Loader';
import Typography from '~/shared/components/Typography';
import { DatafySwitchContext } from '~/shared/context/DatafySwitchProvider';
import { TABLE_LIVE_REFRESH_MS } from '~/shared/constants/polling';
import Tabs, { Tab } from '~/shared/components/Tabs';
import useUtilizationQueryParams from '~/shared/components/UtilizationCharts/useUtilizationQueryParams';

import { baseUrl, datafyErrorToast } from '../FleetManagerPage/helpers';
import { utilizationDateFilterKey } from '../common/serachParamKeys';

import DatafyVolumes, { Counter } from './DatafyVolumes';
import Utilization from './Utilization';
import Details from './Details';
import VolumeActionsLog from './VolumeActionsLog';
import VolumeTagsTable from './VolumeTagsTable';
// import { getFilterSearchParams } from '../common/helpers';

export default function VolumePage() {
  const [hasNoData, setHasNoData] = useState(false);
  const { id: volumeId } = useParams();
  const [searchParams] = useSearchParams();
  const activeDateFilter = searchParams.get(utilizationDateFilterKey);

  // Actions Log State
  const [offset, setOffset] = useState(0);
  const [sortField, setSortField] = useState<{ field: string; dir: 'desc' | 'asc' }>({
    field: 'actionFinishTime',
    dir: 'desc',
  });
  const dataLimit = 50;
  const [activeTab, setActiveTab] = useState<'details' | 'tags'>('details');

  const qs = new URLSearchParams({
    filters: JSON.stringify({
      field: 'volumeId',
      value: volumeId,
      op: 'equals',
    }),
  });

  const { datafySwitchVolumes, removeDatafySwitchVolume } = useContext(DatafySwitchContext);

  const { startDate, endDate } = useUtilizationQueryParams({
    utilizationDateFilterSearchParam: activeDateFilter,
    showFullGraphSearchParam: null,
    volumeId,
  });

  const api = useXFetch();
  const { data, isLoading, error } = useQuery({
    queryKey: ['getVolume', volumeId],
    queryFn: async () => {
      const res = await api<VolumeResponse>(`${baseUrl}?${qs.toString()}`);
      const currentVolume = res.volumes[0];
      const prevVolume = data;
      const machineVolume = datafySwitchVolumes?.find((v) => v.volumeId === volumeId);
      if (!currentVolume) {
        setHasNoData(true);
      }

      if (currentVolume.monitoring !== prevVolume?.monitoring) {
        const datafyProcessFailed =
          !machineVolume &&
          ((currentVolume?.monitoring === 'Monitored' && prevVolume?.monitoring === 'Migrating') ||
            (currentVolume?.monitoring === 'Managed' && prevVolume?.monitoring === 'Unmigrating'));

        if (datafyProcessFailed) {
          datafyErrorToast(
            currentVolume.volumeId,
            currentVolume?.monitoring === 'Monitored' ? 'datafying' : 'undatafying'
          );
        }

        const datafyProcessSucceeded =
          (machineVolume?.state === 'datafying' && currentVolume?.monitoring === 'Managed') ||
          (machineVolume?.state === 'undatafying' && currentVolume?.monitoring === 'Monitored') ||
          (currentVolume?.monitoring === 'Migrating' && prevVolume?.monitoring === 'Monitored') ||
          (currentVolume?.monitoring === 'Unmigrating' && prevVolume?.monitoring === 'Managed');

        if (datafyProcessSucceeded) {
          removeDatafySwitchVolume(currentVolume?.volumeId);
        }
      }

      return currentVolume;
    },
    refetchOnWindowFocus: false,
    refetchInterval: !hasNoData ? TABLE_LIVE_REFRESH_MS : false,
  });

  useEffect(() => {
    if (error) {
      setHasNoData(true);
    }
  }, [error]);

  const actionsLogFilters = useMemo(() => {
    const filter = JSON.stringify({
      field: 'volumeId',
      value: volumeId,
      op: 'equals',
    });
    return [filter];
  }, [volumeId]);

  const tableQueryUrl = useMemo(() => {
    const params = new URLSearchParams([
      ['start_date', startDate],
      ['end_date', endDate],
      ...actionsLogFilters.map((f) => ['filters', f] as [string, string]),
      [
        'sortBy',
        JSON.stringify({
          field: sortField.field,
          ascending: sortField.dir === 'asc',
        }),
      ],
      ['limit', dataLimit.toString()],
      ['offset', offset.toString()],
    ]);

    return `/api/v1/opt-log/actions?${params.toString()}`;
  }, [dataLimit, offset, sortField, actionsLogFilters, startDate, endDate]);

  const countQueryUrl = useMemo(() => {
    const params = new URLSearchParams([
      ['start_date', startDate],
      ['end_date', endDate],
      ...actionsLogFilters.map((f) => ['filters', f] as [string, string]),
    ]);

    return `/api/v1/opt-log/actions/count?${params.toString()}`;
  }, [actionsLogFilters, startDate, endDate]);

  // Tab Configuration
  const tabs: Tab<'details' | 'tags'>[] = [
    {
      label: 'Details',
      value: 'details',
    },
    {
      label: 'Volume tags',
      value: 'tags',
      right: <Counter count={data?.tags?.length ?? 0} />,
    },
  ];

  if (isLoading) {
    return (
      <div className="flex flex-1 justify-center items-center">
        <Loader variant="xl" />
      </div>
    );
  }

  if (data && volumeId && !hasNoData) {
    return (
      <PageContainer className="px-[24px] 2xl:px-[30px] py-6 bg-pale-yello">
        <div className="flex1 grid grid-cols-2 gap[14px] bg-red50 ">
          <div
            className="col-span-2 grid grid-cols-2 bg-gree overflow-hidden h-[418px "
            style={{
              height:
                'clamp(392px, calc(392px + (410 - 392) * ((100vw - 1280px) / (1440 - 1280))), 410px)',
            }}
          >
            <div className="flex flex-col px-6 col-span-1 overflow-hidden">
              <Tabs
                tabs={tabs}
                activeTab={activeTab}
                onChange={setActiveTab}
                containerClassName="-mt-5"
              />

              {activeTab === 'details' ? (
                <Details volume={data} />
              ) : (
                <div className=" bg-carrot-orang">
                  <VolumeTagsTable volume={data} />
                </div>
              )}
            </div>

            <div className="px-6 col-span-1 overflow-hidden ">
              <DatafyVolumes volume={data} refreshKey={data?.volumeId} />
            </div>
          </div>

          <div className="my2 border-b border-primary-70 col-span-2" />

          <Utilization volumeId={data?.volumeId} />

          <div className="col-span-2">
            <VolumeActionsLog
              tableQueryUrl={tableQueryUrl}
              countQueryUrl={countQueryUrl}
              onPageChange={(page) => setOffset(page * dataLimit)}
              onSortChange={setSortField}
              dataLimit={dataLimit}
              currentSortField={sortField}
              currentOffset={offset.toString()}
              timeSignature=""
              filtersSignature={`${actionsLogFilters.join(',')}-${startDate}-${endDate}`}
            />
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <div className="h-full flex items-center justify-center">
      <Typography variant="H5">No Results</Typography>
    </div>
  );
}
